import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import {
  EventFormValues,
  EMPTY_EVENT_FORM,
  toLocalDatetimeValue,
  EventFormFields,
} from '../components/events/EventFormFields.tsx';

// ─── Page ─────────────────────────────────────────────────────────────────────

export function EventFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const [form, setForm] = useState<EventFormValues>(EMPTY_EVENT_FORM);

  // Load existing event in edit mode
  const { data: existing } = useQuery({
    queryKey: ['event-form', id],
    queryFn: () =>
      api.get(`/api/v1/events/${id}`).then((r) => r.data),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing) {
      setForm({
        name: existing.name ?? '',
        description: existing.description ?? '',
        startsAt: toLocalDatetimeValue(existing.startsAt),
        endsAt: toLocalDatetimeValue(existing.endsAt),
        locationName: existing.locationName ?? '',
        locationAddress: existing.locationAddress ?? '',
        maxCapacity: existing.maxCapacity != null ? String(existing.maxCapacity) : '',
        registrationDeadline: toLocalDatetimeValue(existing.registrationDeadline),
        imageUrl: existing.imageUrl ?? '',
      });
    }
  }, [existing]);

  const saveMutation = useMutation({
    mutationFn: (body: object) =>
      isEdit
        ? api.put(`/api/v1/events/${id}`, body).then((r) => r.data)
        : api.post('/api/v1/events', body).then((r) => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['events'] });
      toast.success(isEdit ? 'Event updated' : 'Event created');
      navigate(`/events/${isEdit ? id : data.id}`);
    },
    onError: (err) => toast.error(extractError(err, 'Error saving event')),
  });

  function handleSubmit() {
    if (!form.name || !form.startsAt) return;
    saveMutation.mutate({
      name: form.name,
      description: form.description || undefined,
      startsAt: form.startsAt,
      endsAt: form.endsAt || undefined,
      locationName: form.locationName || undefined,
      locationAddress: form.locationAddress || undefined,
      maxCapacity: form.maxCapacity ? parseInt(form.maxCapacity, 10) : undefined,
      registrationDeadline: form.registrationDeadline || undefined,
      imageUrl: form.imageUrl || undefined,
    });
  }

  function set(key: keyof EventFormValues, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  return (
    <div>
      <PageHeader
        title={isEdit ? 'Edit Event' : 'New Event'}
      />

      <div className="max-w-2xl bg-white border border-zinc-200 rounded-xl p-6 space-y-4">
        <EventFormFields values={form} onChange={(k, v) => set(k, v)} />

        {saveMutation.isError && (
          <p className="text-xs text-red-500">{extractError(saveMutation.error)}</p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            loading={saveMutation.isPending}
            disabled={!form.name || !form.startsAt}
            onClick={handleSubmit}
          >
            {isEdit ? 'Save changes' : 'Create Event'}
          </Button>
        </div>
      </div>
    </div>
  );
}
